import React from "react";
import type { Database } from "@/integrations/supabase/types";
import { format } from "date-fns";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";

type SubscriptionTier = Database['public']['Enums']['subscription_tier'];

const PRICING_TIERS = {
  free: { price: 0, name: "Free" },
  basic: { price: 100, name: "Basic" },
  growth: { price: 250, name: "Growth" },
  professional: { price: 500, name: "Professional" },
} as const;

interface SubscriptionInfoProps {
  subscriptionTier: SubscriptionTier;
  subscriptionUpdatedAt: string | null;
  billingStatus?: string;
}

export const SubscriptionInfo = ({ 
  subscriptionTier, 
  subscriptionUpdatedAt,
  billingStatus = 'active'
}: SubscriptionInfoProps) => {
  const currentTier = PRICING_TIERS[subscriptionTier];
  const nextBillingDate = subscriptionUpdatedAt 
    ? format(new Date(subscriptionUpdatedAt), 'MMMM do, yyyy')
    : 'N/A';
  const isInactive = billingStatus === 'inactive';

  return (
    <>
      {isInactive && (
        <Alert variant="destructive" className="mb-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Your account is currently inactive. Please update your billing information to continue using all features.
          </AlertDescription>
        </Alert>
      )}
      
      <div className="space-y-1">
        <p className="text-sm font-medium">Subscription Plan</p>
        <p className="text-sm text-gray-500">
          {currentTier.name} (${currentTier.price}/month)
        </p>
      </div>

      <div className="space-y-1">
        <p className="text-sm font-medium">Next Billing Date</p>
        <p className="text-sm text-gray-500">{nextBillingDate}</p>
      </div>

      <div className="space-y-1">
        <p className="text-sm font-medium">Billing Status</p>
        <p className={`text-sm ${isInactive ? 'text-red-500 font-medium' : 'text-green-500'}`}>
          {billingStatus === 'inactive' ? 'Inactive' : 'Active'}
        </p>
      </div>
    </>
  );
};